import { memo, useCallback, useState } from 'react';

import { TriangleAlertIcon } from 'lucide-react';

import ConfirmDialog, {
  type ConfirmDialogProps,
} from '@/components/atoms/ConfirmDialog/ConfirmDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';

type Props = Pick<
  ConfirmDialogProps,
  'title' | 'subtitle' | 'primaryButtonText' | 'secondaryButtonText' | 'onConfirm' | 'danger'
> & { callout?: { children?: React.ReactNode; variant?: 'error' | 'warning' } };

const ModalConfirmation = ({ callout, onConfirm, ...props }: Props) => {
  // Callers pop this modal from their own mutation callbacks, so keep the button spinning
  // until they unmount us rather than closing on confirm.
  const [isLoading, setIsLoading] = useState(false);
  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      await onConfirm?.();
    } catch (error) {
      // Stop spinning so the dialog stays usable — the caller never unmounts us on failure.
      setIsLoading(false);
      throw error;
    }
  }, [onConfirm]);

  return (
    <ConfirmDialog
      {...props}
      onConfirm={onConfirm && handleConfirm}
      isLoading={isLoading}
      closeOnConfirm={false}
    >
      {callout?.children && (
        <Alert variant={callout.variant === 'warning' ? 'warning' : 'destructive'}>
          <TriangleAlertIcon size={16} />
          <AlertDescription>{callout.children}</AlertDescription>
        </Alert>
      )}
    </ConfirmDialog>
  );
};

export default memo(ModalConfirmation);
